import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  GraduationCap, Clock, BookOpen, ChevronRight, Award, Sparkles,
  ArrowLeft, Play, Flame, MessageCircle, Trophy, Search, Tag,
  ArrowRight, Users, Star, LogOut, ChevronDown, Volume2, VolumeX,
} from "lucide-react";
import QuickBitesSection from "../components/QuickBitesSection";
import ShansoLearnComparison from "../components/ShansoLearnComparison";
import { loginWithGoogle as startGoogleLogin } from "../lib/userAuth";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const ACCENT = "#22D3EE";
const ACCENT_DEEP = "#0891B2";

function getAuthConfig() {
  const tok = localStorage.getItem("ai_tools_token");
  return tok ? { headers: { Authorization: `Bearer ${tok}` } } : {};
}

const FILTERS = [
  { id: "all", label: "All courses" },
  { id: "in_progress", label: "In progress" },
  { id: "completed", label: "Completed" },
  { id: "new", label: "New" },
];

// Hardcoded category chips for now — populated from course tags later.
const CATEGORIES = [
  { id: "all", label: "All", icon: Sparkles },
  { id: "ai", label: "AI & LLMs", icon: Sparkles },
  { id: "career", label: "Career", icon: Trophy },
  { id: "coding", label: "Coding", icon: BookOpen },
  { id: "productivity", label: "Productivity", icon: Flame },
];

function loadLocalEnrollments() {
  try {
    return JSON.parse(localStorage.getItem("shansolearn_enrollments") || "{}");
  } catch { return {}; }
}

export default function CoursesCatalogPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [enrollments, setEnrollments] = useState(loadLocalEnrollments());
  const [enrollmentStates, setEnrollmentStates] = useState({}); // slug → state from /api/learn

  // 1. Fetch all published courses with server-side search + category filters.
  //    Debounce only after the initial mount so the first paint isn't delayed.
  const isFirstLoad = useRef(true);
  useEffect(() => {
    const fetchNow = () => {
      setLoading(true);
      const params = new URLSearchParams();
      if (search.trim()) params.set("q", search.trim());
      if (category && category !== "all") params.set("category", category);
      axios.get(`${API}/courses?${params.toString()}`)
        .then((r) => setItems(r.data.items || []))
        .finally(() => setLoading(false));
    };
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      fetchNow();
      return undefined;
    }
    const handle = setTimeout(fetchNow, 250);
    return () => clearTimeout(handle);
  }, [search, category]);

  // 1a. Fetch live category list + bundle deals once on mount
  const [categories, setCategories] = useState([]);
  const [bundles, setBundles] = useState([]);
  useEffect(() => {
    axios.get(`${API}/courses/categories`).then((r) => setCategories(r.data.items || [])).catch(() => {});
    axios.get(`${API}/courses/bundles`).then((r) => setBundles(r.data.items || [])).catch(() => {});
  }, []);

  // 1b. If user is logged in via Google, fetch enrollments from backend
  //     and merge into localStorage map so the Continue rail works cross-device.
  const [googleUser, setGoogleUser] = useState(null);
  useEffect(() => {
    const tok = localStorage.getItem("ai_tools_token");
    if (!tok) return;
    axios.get(`${API}/my/course-enrollments`, getAuthConfig())
      .then((r) => {
        setGoogleUser(r.data.user || null);
        const merged = { ...loadLocalEnrollments() };
        (r.data.items || []).forEach((e) => {
          if (!e.token) return;
          merged[e.slug] = {
            slug: e.slug,
            token: e.token,
            full_name: e.full_name,
            email: e.email,
            course_id: e.course_id,
            course_title: e.course_title,
            course_cover: e.course_cover,
            last_visited: e.last_visited,
          };
        });
        localStorage.setItem("shansolearn_enrollments", JSON.stringify(merged));
        setEnrollments(merged);
      })
      .catch(() => { /* 401 expected if not signed in */ });
  }, []);

  // 2. For each stored enrollment, fetch its progress in parallel
  useEffect(() => {
    const slugs = Object.keys(enrollments || {});
    if (!slugs.length) return;
    let cancelled = false;
    (async () => {
      const results = await Promise.all(slugs.map((s) =>
        axios.get(`${API}/learn/${s}?token=${enrollments[s].token}`)
          .then((r) => [s, r.data])
          .catch(() => [s, null])
      ));
      if (cancelled) return;
      const map = {};
      results.forEach(([s, data]) => { if (data) map[s] = data; });
      setEnrollmentStates(map);
    })();
    return () => { cancelled = true; };
  }, [enrollments]);

  // 3. Build view models
  const enrichedCourses = useMemo(() => {
    return (items || []).map((c) => {
      const enr = enrollments[c.slug];
      const st = enrollmentStates[c.slug];
      let progress_pct = 0;
      let modules_done = 0;
      let last_module_no = null;
      let next_module_no = null;
      let status = "not_enrolled"; // not_enrolled | enrolled | in_progress | completed
      if (enr) {
        status = "enrolled";
        if (st && st.modules) {
          const total = st.modules.length || 1;
          modules_done = st.modules.filter((m) => m.quiz_passed).length;
          progress_pct = Math.round((modules_done / total) * 100);
          const next = st.modules.find((m) => m.unlocked && !m.quiz_passed) || st.modules.find((m) => m.unlocked);
          next_module_no = next?.module_no ?? null;
          last_module_no = next_module_no;
          if (st.exam_passed || st.certificate_id) status = "completed";
          else if (modules_done > 0 || (st.modules[0]?.video_watched_pct || 0) > 5) status = "in_progress";
        }
      }
      return {
        ...c,
        _enr: enr || null,
        _st: st || null,
        progress_pct,
        modules_done,
        next_module_no,
        last_module_no,
        status,
      };
    });
  }, [items, enrollments, enrollmentStates]);

  // 4. Filtered list — `filter` chip is still client-side (status-aware filter
  //    needs the enriched data), but `search` + `category` are already applied
  //    server-side via the params above.
  const filteredCourses = useMemo(() => {
    return enrichedCourses.filter((c) => {
      if (filter === "in_progress" && c.status !== "in_progress") return false;
      if (filter === "completed" && c.status !== "completed") return false;
      if (filter === "new" && (c.status === "in_progress" || c.status === "completed")) return false;
      return true;
    });
  }, [enrichedCourses, filter]);

  const continueLearning = enrichedCourses.filter((c) => c.status === "in_progress" || c.status === "enrolled");
  const completed = enrichedCourses.filter((c) => c.status === "completed");
  const greetingName = Object.values(enrollments)[0]?.full_name?.split(" ")?.[0] || null;
  const featured = enrichedCourses[0];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* ── Top bar ─────────────────────────────────────── */}
      <header className="border-b border-zinc-800/60 bg-[#0a0a0a]/85 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <Link to="/" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white text-sm font-medium shrink-0" data-testid="back-to-home">
            <ArrowLeft size={16} /> Home
          </Link>
          <div className="flex items-center gap-3 min-w-0">
            <div className="hidden sm:flex items-center gap-2 shrink-0" style={{ color: ACCENT }}>
              <GraduationCap size={18} />
              <span className="font-semibold tracking-tight text-sm">ShansoLearn</span>
            </div>
            {(Object.keys(enrollments).length > 0 || googleUser) && (
              <Link
                to="/my-courses"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cyan-500/15 border border-cyan-500/40 text-cyan-200 hover:bg-cyan-500/25 transition text-xs sm:text-sm font-bold shrink-0"
                data-testid="my-courses-header-link"
              >
                <BookOpen size={14} /> My Courses
              </Link>
            )}
            {googleUser && (
              <AccountPill user={googleUser} onSignOut={async () => {
                try { await axios.post(`${API}/user-auth/logout`, {}, getAuthConfig()); } catch (_) { /* ignore */ }
                localStorage.removeItem("ai_tools_token");
                sessionStorage.removeItem("user_data");
                localStorage.removeItem("shansolearn_enrollments");
                window.location.reload();
              }} />
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 pt-8 pb-16 space-y-10">
        {/* ── Hero strip ──────────────────────────────────── */}
        <section className="space-y-5" data-testid="hero-strip">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6 lg:gap-10 items-center">
            {/* LEFT — Headline + value props */}
            <div>
              <p className="inline-flex items-center gap-2 text-sm sm:text-base uppercase tracking-widest text-cyan-300 font-bold mb-3" data-testid="hero-eyebrow">
                <Sparkles size={16} /> Self-paced video courses
              </p>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.05]">
                {greetingName ? (
                  <>Welcome back, <span style={{ color: ACCENT }}>{greetingName}</span>.</>
                ) : (
                  <>Learn AI skills at your <span style={{ color: ACCENT }}>own pace</span>.</>
                )}
              </h1>
              <p className="text-zinc-400 text-sm sm:text-base mt-2 max-w-2xl">
                {greetingName
                  ? "Pick up where you left off, or start something new."
                  : "Premium video courses · Module quizzes · Final assessment · Auto-emailed certificate."}
              </p>

              {/* Trust badge strip — warm reassurance for self-paced learners */}
              <div className="mt-5 flex flex-wrap gap-2.5" data-testid="hero-trust-badges">
                <span className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-cyan-500/15 border border-cyan-500/50 text-cyan-100 text-sm font-bold">
                  <Clock size={15} /> Self-paced
                </span>
                <span className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-violet-500/15 border border-violet-500/50 text-violet-100 text-sm font-bold">
                  <Sparkles size={15} /> Lifetime access
                </span>
                <span className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-amber-500/15 border border-amber-500/50 text-amber-100 text-sm font-bold">
                  <Award size={15} /> Certificate on completion
                </span>
                <span className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-emerald-500/15 border border-emerald-500/50 text-emerald-100 text-sm font-bold">
                  <Play size={15} /> Watch on any device
                </span>
              </div>
            </div>

            {/* RIGHT — Cinematic intro video (autoplay, muted, looped) */}
            <HeroIntroVideo />
          </div>

          {/* "Why Self-Paced" reassurance strip — warm + scannable, only for new visitors */}
          {!greetingName && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="why-self-paced-strip">
              <div className="rounded-xl border border-cyan-500/20 bg-gradient-to-br from-cyan-500/[0.06] to-transparent p-4">
                <div className="w-9 h-9 rounded-lg bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center text-cyan-300 mb-2.5">
                  <Clock size={16} />
                </div>
                <p className="text-white text-sm font-bold leading-tight mb-1">Learn in your rhythm</p>
                <p className="text-zinc-400 text-xs leading-relaxed">
                  Watch 5 minutes at 7am or 2 hours on Sunday — your call. Pause, replay, skip ahead. The course bends to your schedule, not the other way around.
                </p>
              </div>
              <div className="rounded-xl border border-violet-500/20 bg-gradient-to-br from-violet-500/[0.06] to-transparent p-4">
                <div className="w-9 h-9 rounded-lg bg-violet-500/15 border border-violet-500/30 flex items-center justify-center text-violet-300 mb-2.5">
                  <Sparkles size={16} />
                </div>
                <p className="text-white text-sm font-bold leading-tight mb-1">Lifetime access, no FOMO</p>
                <p className="text-zinc-400 text-xs leading-relaxed">
                  Buy once. Revisit anytime — including future updates and new modules. Life got busy? Come back next month. Or next year. It&rsquo;s all here for you.
                </p>
              </div>
              <div className="rounded-xl border border-amber-500/20 bg-gradient-to-br from-amber-500/[0.06] to-transparent p-4">
                <div className="w-9 h-9 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center text-amber-300 mb-2.5">
                  <Trophy size={16} />
                </div>
                <p className="text-white text-sm font-bold leading-tight mb-1">Certified, never pressured</p>
                <p className="text-zinc-400 text-xs leading-relaxed">
                  Take the quiz when you feel ready. Earn your certificate when you&rsquo;re confident — no deadlines, no cohort stress, no race.
                </p>
              </div>
            </div>
          )}

          {/* Search + filter row */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 max-w-md">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search courses…"
                className="w-full bg-zinc-900/70 border border-zinc-800 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-cyan-500/60 transition-colors"
                data-testid="course-search-input"
              />
            </div>
            <div className="flex gap-1.5 overflow-x-auto no-scrollbar -mx-1 px-1">
              {FILTERS.map((f) => {
                const active = filter === f.id;
                return (
                  <button
                    key={f.id}
                    onClick={() => setFilter(f.id)}
                    className={`shrink-0 px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                      active
                        ? "bg-cyan-500/15 border-cyan-500/60 text-cyan-200"
                        : "border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-zinc-200"
                    }`}
                    data-testid={`filter-${f.id}`}
                  >
                    {f.label}
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── Sign-in-with-Google CTA (when not logged in AND no local enrollments) ── */}
        {!googleUser && Object.keys(enrollments || {}).length === 0 && (
          <GoogleSignInCTA />
        )}

        {/* ── Continue Learning rail ──────────────────────── */}
        {continueLearning.length > 0 && (
          <section data-testid="continue-rail">
            <div className="flex items-end justify-between mb-3">
              <div>
                <h2 className="text-white text-xl font-bold inline-flex items-center gap-2">
                  <Play size={16} className="text-cyan-300" /> Continue learning
                </h2>
                <p className="text-zinc-500 text-xs">Resume where you left off · {continueLearning.length} active {continueLearning.length === 1 ? "course" : "courses"}</p>
              </div>
            </div>
            <div className="flex gap-4 overflow-x-auto no-scrollbar -mx-1 px-1 pb-2 snap-x">
              {continueLearning.map((c) => (
                <ContinueCard key={c.course_id} course={c} />
              ))}
            </div>
          </section>
        )}

        {/* ── Featured course banner (only when not enrolled in it) ── */}
        {featured && featured.status === "not_enrolled" && (
          <FeaturedCourseBanner course={featured} />
        )}

        {/* ── Categories chip row (live from backend) ────── */}
        <section data-testid="category-chips">
          <p className="text-zinc-500 text-[10px] uppercase tracking-widest font-bold mb-2">Browse by category</p>
          <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1">
            {[{ id: "all", label: "All", count: items.length }, ...CATEGORIES.filter((c) => c.id !== "all").map((c) => ({
              ...c,
              count: categories.find((bc) => bc.id === c.id)?.count || 0,
            }))].map((cat) => {
              const active = category === cat.id;
              const Icon = CATEGORIES.find((c) => c.id === cat.id)?.icon || Sparkles;
              const disabled = cat.id !== "all" && cat.count === 0;
              return (
                <button
                  key={cat.id}
                  onClick={() => !disabled && setCategory(cat.id)}
                  disabled={disabled}
                  className={`shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border text-xs font-semibold transition-colors ${
                    active
                      ? "bg-cyan-500/15 border-cyan-500/60 text-cyan-200"
                      : disabled
                        ? "border-zinc-900 text-zinc-600 cursor-not-allowed opacity-50"
                        : "border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-zinc-200"
                  }`}
                  data-testid={`cat-${cat.id}`}
                >
                  <Icon size={12} /> {cat.label}
                  {cat.count > 0 && (
                    <span className={`ml-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold ${active ? "bg-cyan-500/30 text-cyan-100" : "bg-zinc-800 text-zinc-400"}`}>
                      {cat.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </section>

        {/* ── All courses grid ────────────────────────────── */}
        <section data-testid="all-courses-section">
          <div className="flex items-end justify-between mb-3">
            <div>
              <h2 className="text-white text-xl font-bold">All courses</h2>
              <p className="text-zinc-500 text-xs">
                {loading
                  ? "Loading…"
                  : `${filteredCourses.length} ${filteredCourses.length === 1 ? "course" : "courses"} ${filter !== "all" ? `· filter: ${FILTERS.find(f => f.id === filter)?.label}` : ""}`}
              </p>
            </div>
          </div>

          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {[1,2,3].map((i) => <SkeletonCard key={i} />)}
            </div>
          ) : filteredCourses.length === 0 ? (
            <EmptyState filter={filter} search={search} />
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="course-grid">
              {filteredCourses.map((c) => <CourseCard key={c.course_id} course={c} />)}
            </div>
          )}
        </section>

        {/* ── Completed (achievements) ──────────────────── */}
        {completed.length > 0 && (
          <section data-testid="completed-section">
            <h2 className="text-white text-xl font-bold inline-flex items-center gap-2 mb-3">
              <Trophy size={16} className="text-amber-300" /> Your certificates
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {completed.map((c) => (
                <Link
                  key={c.course_id}
                  to={`/learn/${c.slug}?token=${c._enr.token}`}
                  className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-4 hover:bg-amber-500/[0.1] transition-colors"
                  data-testid={`cert-card-${c.slug}`}
                >
                  <div className="flex items-center gap-3">
                    <Award size={28} className="text-amber-300 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-white text-sm font-bold truncate">{c.title}</p>
                      <p className="text-amber-200/80 text-xs">Certified · view certificate →</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* ── Bundle / promo card (live from backend) ─── */}
        {bundles.length > 0 && bundles.map((b) => <BundleBanner key={b.bundle_id} bundle={b} allCourses={items} />)}

        {/* ── Quick Bites (⚡ 80-min sprints) ─────────────── */}
        <QuickBitesSection />

        {/* ── Why ShansoLearn (SEO comparison block) ──────── */}
        <ShansoLearnComparison />

        {/* ── Community / Support footer ─────────────────── */}
        <SupportFooter />
      </main>
    </div>
  );
}

/* ─── Continue Learning horizontal card ───────────────────── */
function ContinueCard({ course: c }) {
  const url = `/learn/${c.slug}?token=${c._enr.token}`;
  const pct = c.progress_pct;
  return (
    <Link
      to={url}
      className="snap-start shrink-0 w-[280px] sm:w-[320px] rounded-2xl overflow-hidden border border-zinc-800 bg-zinc-900/50 hover:border-cyan-500/50 hover:bg-zinc-900 transition-all group"
      data-testid={`continue-card-${c.slug}`}
    >
      <div className="aspect-video bg-zinc-900 overflow-hidden relative">
        {c.cover_image_url ? (
          <img src={c.cover_image_url} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-cyan-500/20 via-zinc-900 to-violet-500/20 flex items-center justify-center">
            <GraduationCap size={40} className="text-cyan-400/40" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
        <div className="absolute bottom-2 left-3 right-3 flex items-center gap-2">
          <span className="px-2 py-0.5 rounded-full bg-cyan-500/90 text-black text-[10px] font-bold">RESUME</span>
          <span className="text-white text-[11px] font-semibold drop-shadow">
            {c.next_module_no ? `Module ${c.next_module_no}` : "Start"}
          </span>
        </div>
      </div>
      <div className="p-4">
        <p className="text-white text-base font-bold leading-tight line-clamp-2 group-hover:text-cyan-100">{c.title}</p>
        <div className="mt-3">
          <div className="flex items-center justify-between mb-1.5 text-[11px]">
            <span className="text-zinc-400">{c.modules_done} of {c._st?.modules?.length || c.modules_count || 0} modules</span>
            <span className="text-cyan-300 font-bold">{pct}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-zinc-800 overflow-hidden">
            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_DEEP})` }} />
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between">
          <span className="text-zinc-500 text-[11px]">Last opened recently</span>
          <span className="inline-flex items-center gap-1 text-cyan-300 text-xs font-bold">
            Continue <ChevronRight size={12} />
          </span>
        </div>
      </div>
    </Link>
  );
}

/* ─── Featured course banner ──────────────────────────────── */
function FeaturedCourseBanner({ course: c }) {
  const discount = c.original_price_inr && c.original_price_inr > c.price_inr
    ? Math.round(((c.original_price_inr - c.price_inr) / c.original_price_inr) * 100)
    : null;
  return (
    <Link
      to={`/courses/${c.slug}`}
      className="block rounded-2xl overflow-hidden border border-cyan-500/30 bg-gradient-to-br from-cyan-900/30 via-zinc-900/60 to-fuchsia-900/20 hover:border-cyan-500/50 transition-colors group"
      data-testid="featured-course-banner"
    >
      <div className="grid sm:grid-cols-[1.2fr,1fr] gap-0">
        <div className="p-5 sm:p-7 flex flex-col justify-center">
          <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-fuchsia-300 mb-2">
            <Sparkles size={11} /> Flagship course
          </span>
          <p className="text-white text-2xl sm:text-3xl font-extrabold leading-tight">{c.title}</p>
          {c.summary && <p className="text-zinc-300 text-sm mt-2 line-clamp-2">{c.summary}</p>}
          <div className="flex items-center gap-3 text-xs text-zinc-400 mt-3 flex-wrap">
            <span className="inline-flex items-center gap-1"><BookOpen size={11} /> {c.modules_count || 0} modules</span>
            {c.duration_min_total && <span className="inline-flex items-center gap-1"><Clock size={11} /> {Math.round(c.duration_min_total / 60)}h</span>}
            <span className="inline-flex items-center gap-1"><Award size={11} /> Certificate</span>
            <span className="inline-flex items-center gap-1 text-cyan-300"><Play size={11} /> Self-paced</span>
            <span className="inline-flex items-center gap-1 text-amber-300"><Star size={11} fill="currentColor" /> Bestseller</span>
          </div>
          <div className="mt-4 flex items-center gap-4 flex-wrap">
            <div>
              <span className="text-cyan-300 font-extrabold text-2xl">₹{c.price_inr}</span>
              {c.original_price_inr && c.original_price_inr > c.price_inr && (
                <span className="text-zinc-500 text-sm line-through ml-2">₹{c.original_price_inr}</span>
              )}
            </div>
            {discount && (
              <span className="px-2 py-0.5 rounded-full bg-fuchsia-500/15 border border-fuchsia-500/40 text-fuchsia-200 text-[10px] font-bold">{discount}% OFF</span>
            )}
          </div>
          <span className="mt-4 inline-flex items-center gap-1.5 text-cyan-300 text-sm font-bold group-hover:gap-2.5 transition-all w-fit">
            View course <ArrowRight size={14} />
          </span>
        </div>
        <div className="hidden sm:block bg-zinc-950/60 relative overflow-hidden">
          {c.cover_image_url ? (
            <img src={c.cover_image_url} alt={c.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-cyan-500/30 to-fuchsia-500/20 flex items-center justify-center">
              <GraduationCap size={80} className="text-cyan-300/40" />
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}

/* ─── Course card (state-aware) ───────────────────────────── */
function CourseCard({ course: c }) {
  const discount = c.original_price_inr && c.original_price_inr > c.price_inr
    ? Math.round(((c.original_price_inr - c.price_inr) / c.original_price_inr) * 100)
    : null;
  const enrolled = c.status !== "not_enrolled";
  const completed = c.status === "completed";
  const inProgress = c.status === "in_progress";
  const ctaText = completed ? "View certificate" : inProgress ? "Continue" : enrolled ? "Start learning" : "View course";
  const href = enrolled ? `/learn/${c.slug}?token=${c._enr.token}` : `/courses/${c.slug}`;

  return (
    <Link
      to={href}
      className="group rounded-2xl overflow-hidden border border-zinc-800 bg-zinc-900/40 hover:border-cyan-500/50 hover:bg-zinc-900/70 hover:-translate-y-0.5 transition-all flex flex-col"
      data-testid={`course-card-${c.slug}`}
    >
      <div className="aspect-video bg-zinc-900 overflow-hidden relative">
        {c.cover_image_url ? (
          <img src={c.cover_image_url} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-cyan-500/20 via-zinc-900 to-violet-500/20 flex items-center justify-center">
            <GraduationCap size={48} className="text-cyan-400/40" />
          </div>
        )}
        {/* badges */}
        <div className="absolute top-2.5 left-2.5 flex flex-col gap-1.5 items-start">
          {!enrolled && discount && (
            <span className="px-2 py-0.5 rounded-full bg-fuchsia-500/95 text-white text-[10px] font-bold shadow">{discount}% OFF</span>
          )}
          {completed && (
            <span className="px-2 py-0.5 rounded-full bg-amber-500/95 text-black text-[10px] font-bold shadow inline-flex items-center gap-1"><Trophy size={10} /> CERTIFIED</span>
          )}
          {inProgress && (
            <span className="px-2 py-0.5 rounded-full bg-cyan-500/95 text-black text-[10px] font-bold shadow">IN PROGRESS</span>
          )}
        </div>
      </div>

      <div className="p-4 flex flex-col flex-1">
        <p className="text-white font-bold text-base leading-snug line-clamp-2 group-hover:text-cyan-100 mb-1.5">{c.title}</p>
        <div className="flex items-center gap-2.5 text-[11px] text-zinc-500 mb-3 flex-wrap">
          <span className="inline-flex items-center gap-1"><BookOpen size={11} /> {c.modules_count || 0}</span>
          {c.duration_min_total && (
            <span className="inline-flex items-center gap-1"><Clock size={11} /> {Math.round(c.duration_min_total / 60)}h</span>
          )}
          <span className="inline-flex items-center gap-1"><Award size={11} /> Certificate</span>
          <span className="inline-flex items-center gap-1 text-cyan-300/80" data-testid="self-paced-pill">
            <Play size={10} /> Self-paced
          </span>
        </div>

        {inProgress && (
          <div className="mb-3">
            <div className="flex items-center justify-between text-[10px] mb-1">
              <span className="text-zinc-400 font-semibold">Progress</span>
              <span className="text-cyan-300 font-bold">{c.progress_pct}%</span>
            </div>
            <div className="h-1 rounded-full bg-zinc-800 overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${c.progress_pct}%`, background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_DEEP})` }} />
            </div>
          </div>
        )}

        <div className="mt-auto flex items-center justify-between pt-3 border-t border-zinc-800">
          {enrolled ? (
            <span className="inline-flex items-center gap-1 text-emerald-300 text-xs font-bold">
              <Users size={11} /> Enrolled
            </span>
          ) : (
            <div>
              <span className="text-cyan-300 font-extrabold text-lg">₹{c.price_inr}</span>
              {c.original_price_inr && c.original_price_inr > c.price_inr && (
                <span className="text-zinc-600 text-xs line-through ml-1.5">₹{c.original_price_inr}</span>
              )}
            </div>
          )}
          <span className={`text-xs font-bold inline-flex items-center gap-0.5 group-hover:gap-1.5 transition-all ${
            completed ? "text-amber-300" : "text-cyan-300"
          }`}>
            {ctaText} <ChevronRight size={14} />
          </span>
        </div>
      </div>
    </Link>
  );
}

function SkeletonCard() {
  return (
    <div className="rounded-2xl overflow-hidden border border-zinc-800 bg-zinc-900/30 animate-pulse">
      <div className="aspect-video bg-zinc-800/50" />
      <div className="p-4 space-y-2">
        <div className="h-4 bg-zinc-800/70 rounded w-3/4" />
        <div className="h-3 bg-zinc-800/50 rounded w-1/2" />
        <div className="h-3 bg-zinc-800/50 rounded w-2/3" />
      </div>
    </div>
  );
}

function EmptyState({ filter, search }) {
  const msg = search
    ? `No courses match "${search}".`
    : filter === "in_progress" ? "You don't have any courses in progress."
    : filter === "completed" ? "No certificates yet — finish a course to earn one."
    : filter === "new" ? "No new courses right now. Check back soon!"
    : "No courses live yet — drop back soon.";
  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-900/30 p-10 text-center" data-testid="empty-state">
      <BookOpen size={36} className="mx-auto text-zinc-700 mb-3" />
      <p className="text-zinc-300 text-sm font-semibold">{msg}</p>
    </div>
  );
}

import { load as loadCashfree } from "@cashfreepayments/cashfree-js";

// ────────────────────────────────────────────────────────────────────────────
// HeroIntroVideo — cinematic 9-second loop next to the headline.
//
// Behaviour:
//  - Autoplay muted + loop (browsers require muted for autoplay)
//  - Lazy-loaded (only requests bytes when scrolled into view)
//  - Pauses when off-screen (IntersectionObserver) to save battery / data
//  - Tap-to-unmute overlay button (top-right)
//  - Stacks below the headline on mobile, sits on the right on lg+
// ────────────────────────────────────────────────────────────────────────────
function HeroIntroVideo() {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [loaded, setLoaded] = useState(false);

  // Pause when scrolled off-screen, resume when back.
  useEffect(() => {
    const node = containerRef.current;
    if (!node || typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver(
      (entries) => {
        const v = videoRef.current;
        if (!v) return;
        if (entries[0].isIntersecting) v.play().catch(() => {});
        else v.pause();
      },
      { threshold: 0.25 },
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, []);

  function toggleMute() {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  }

  return (
    <div
      ref={containerRef}
      className="relative w-full lg:w-[440px] aspect-video rounded-2xl overflow-hidden border border-zinc-800 shadow-[0_24px_60px_-20px_rgba(34,211,238,0.25)]"
      data-testid="hero-intro-video"
    >
      <video
        ref={videoRef}
        src="/shansolearn-marketing/intro.mp4"
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        onLoadedData={() => setLoaded(true)}
        className="w-full h-full object-cover bg-zinc-950"
      />

      {/* Soft brand glow overlay so the video blends with the dark hero */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-cyan-500/[0.05] via-transparent to-violet-500/[0.05]" />

      {/* Mute / unmute button */}
      <button
        onClick={toggleMute}
        className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/60 backdrop-blur-sm border border-white/15 flex items-center justify-center text-white hover:bg-black/80 transition-colors"
        data-testid="hero-intro-mute-toggle"
        aria-label={muted ? "Unmute" : "Mute"}
      >
        {muted ? <VolumeX size={15} /> : <Volume2 size={15} />}
      </button>

      {/* Tiny "Intro" pill so users know what they're seeing */}
      <span className="absolute top-3 left-3 px-2 py-0.5 rounded-full bg-black/60 backdrop-blur-sm border border-white/15 text-[10px] uppercase tracking-widest text-white/85 font-bold">
        Intro
      </span>

      {/* Skeleton shimmer while bytes stream in */}
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-zinc-900/60">
          <div className="w-10 h-10 rounded-full bg-cyan-500/20 animate-pulse flex items-center justify-center">
            <Play size={18} className="text-cyan-300" />
          </div>
        </div>
      )}
    </div>
  );
}

function BundleBanner({ bundle: b, allCourses = [] }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div
        className="rounded-2xl border border-fuchsia-500/30 bg-gradient-to-br from-fuchsia-900/30 via-zinc-900/40 to-cyan-900/20 p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between"
        data-testid={`bundle-banner-${b.bundle_id}`}
      >
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-12 h-12 rounded-xl bg-fuchsia-500/20 border border-fuchsia-500/40 flex items-center justify-center shrink-0">
            <Tag size={20} className="text-fuchsia-300" />
          </div>
          <div className="min-w-0">
            <p className="text-white text-lg sm:text-xl font-extrabold leading-tight">{b.title}</p>
            <p className="text-zinc-300 text-sm mt-1">{b.tagline}</p>
            <div className="mt-2 inline-flex items-center gap-2 flex-wrap">
              <span className="text-cyan-300 font-extrabold text-base">From ₹{b.bundle_price_inr.toLocaleString("en-IN")}</span>
              <span className="text-zinc-500 text-xs line-through">₹{b.individual_total_inr.toLocaleString("en-IN")}</span>
              <span className="px-2 py-0.5 rounded-full bg-fuchsia-500/20 border border-fuchsia-500/40 text-fuchsia-200 text-[10px] font-bold">up to {b.discount_pct}% OFF</span>
            </div>
          </div>
        </div>
        <button
          onClick={() => setOpen(true)}
          className="shrink-0 inline-flex items-center justify-center px-5 py-2.5 rounded-lg text-black font-bold text-sm whitespace-nowrap"
          style={{ background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_DEEP})` }}
          data-testid={`bundle-cta-${b.bundle_id}`}
        >
          Explore bundle <ArrowRight size={14} className="inline ml-1" />
        </button>
      </div>

      {open && (
        <BundleCheckoutModal
          bundle={b}
          allCourses={allCourses}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}


function BundleCheckoutModal({ bundle: b, allCourses, onClose }) {
  // Step 1 = select courses · Step 2 = info form + pay
  const [step, setStep] = useState(1);
  const initialSelected = useMemo(
    () => new Set((b.sample_courses || []).map((c) => c.slug)),
    [b.sample_courses],
  );
  const [selected, setSelected] = useState(initialSelected);
  const [quote, setQuote] = useState({
    course_count: 0, individual_total_inr: 0, bundle_price_inr: 0,
    savings_inr: 0, discount_pct: 0, courses: [],
  });
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  // Only published courses can be in a bundle.
  const eligibleCourses = useMemo(
    () => (allCourses || []).filter((c) => (c.status || "published") === "published"),
    [allCourses],
  );

  // Recompute quote on selection change (server-authoritative pricing).
  useEffect(() => {
    const slugs = Array.from(selected);
    const handle = setTimeout(() => {
      if (slugs.length === 0) {
        setQuote({ course_count: 0, individual_total_inr: 0, bundle_price_inr: 0, savings_inr: 0, discount_pct: 0, courses: [] });
        return;
      }
      setQuoteLoading(true);
      axios.post(`${API}/courses/bundles/quote`, { slugs })
        .then((r) => setQuote(r.data))
        .catch(() => {})
        .finally(() => setQuoteLoading(false));
    }, 200);
    return () => clearTimeout(handle);
  }, [selected]);

  function toggleCourse(slug) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  }

  async function payAndRegister() {
    setErr("");
    if (selected.size < 2) {
      setErr("Bundle pricing needs ≥2 courses. For a single course, please use the course detail page.");
      return;
    }
    if (!form.full_name.trim() || form.full_name.trim().length < 2) {
      setErr("Please enter your full name (this is printed on your certificates)."); return;
    }
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) {
      setErr("Please enter a valid email address."); return;
    }
    const digits = form.phone.replace(/\D/g, "");
    if (digits.length < 10) { setErr("Please enter a 10-digit mobile number."); return; }

    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/courses/bundles/checkout`, {
        slugs: Array.from(selected),
        full_name: form.full_name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: digits,
        origin_url: window.location.origin,
      }, getAuthConfig());

      if (!r.data?.payment_session_id) throw new Error("Payment session not created");

      // Launch Cashfree drop-in checkout
      const cashfree = await loadCashfree({ mode: "production" });
      cashfree.checkout({
        paymentSessionId: r.data.payment_session_id,
        redirectTarget: "_self",
      });
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Checkout failed. Please try again.");
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid={`bundle-modal-${b.bundle_id}`}
    >
      <div
        className="bg-zinc-900 border border-zinc-700 rounded-2xl max-w-3xl w-full max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-5 sm:p-6 border-b border-zinc-800 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-cyan-300 text-[10px] uppercase tracking-widest font-bold mb-1">Step {step} of 2 · {step === 1 ? "Select courses" : "Your details"}</p>
            <p className="text-white text-xl font-extrabold leading-tight">{b.title}</p>
            <p className="text-zinc-400 text-sm mt-1">Pick any number — bundle discount applies automatically (≥2 to unlock).</p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white text-3xl leading-none shrink-0" data-testid={`bundle-modal-close-${b.bundle_id}`} aria-label="Close">×</button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-4">
          {step === 1 && (
            <>
              <p className="text-zinc-300 text-sm font-semibold">Available courses ({eligibleCourses.length})</p>
              <div className="space-y-2">
                {eligibleCourses.map((c) => {
                  const checked = selected.has(c.slug);
                  return (
                    <label
                      key={c.slug}
                      className={`flex items-center gap-3 rounded-xl border p-3 cursor-pointer transition-all ${
                        checked
                          ? "border-cyan-500/60 bg-cyan-500/5"
                          : "border-zinc-800 bg-zinc-900/50 hover:border-zinc-700"
                      }`}
                      data-testid={`bundle-course-row-${c.slug}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleCourse(c.slug)}
                        className="w-4 h-4 accent-cyan-500 shrink-0"
                        data-testid={`bundle-checkbox-${c.slug}`}
                      />
                      <div className="w-16 h-10 rounded-md overflow-hidden bg-zinc-900 shrink-0">
                        {c.cover_image_url ? (
                          <img src={c.cover_image_url} alt={c.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-cyan-500/20 to-violet-500/20 flex items-center justify-center">
                            <BookOpen size={14} className="text-zinc-600" />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-white text-sm font-bold leading-tight truncate">{c.title}</p>
                        <p className="text-zinc-500 text-xs mt-0.5">
                          {c.modules_count || "—"} modules · ~{c.duration_min_total || "?"} min
                        </p>
                      </div>
                      <p className="text-zinc-300 text-sm font-bold shrink-0">₹{(c.price_inr || 0).toLocaleString("en-IN")}</p>
                    </label>
                  );
                })}
              </div>
            </>
          )}

          {step === 2 && (
            <div className="space-y-4">
              {/* Selected courses summary */}
              <div className="rounded-xl border border-zinc-800 bg-zinc-950/50 p-4">
                <p className="text-zinc-400 text-xs font-bold uppercase tracking-wider mb-2">Your bundle ({quote.course_count} courses)</p>
                <ul className="space-y-1.5">
                  {(quote.courses || []).map((c) => (
                    <li key={c.slug} className="flex items-center justify-between text-sm">
                      <span className="text-zinc-200 truncate pr-2">{c.title}</span>
                      <span className="text-zinc-500 line-through text-xs shrink-0">₹{(c.price_inr || 0).toLocaleString("en-IN")}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-zinc-300 text-xs font-bold mb-1.5">Full name (as it should appear on your certificate)</label>
                  <input
                    type="text"
                    value={form.full_name}
                    onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
                    placeholder="e.g. Priya Sharma"
                    className="w-full bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-cyan-500/60"
                    data-testid="bundle-name-input"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-zinc-300 text-xs font-bold mb-1.5">Email (for course access + certificate)</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    placeholder="you@example.com"
                    className="w-full bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-cyan-500/60"
                    data-testid="bundle-email-input"
                  />
                </div>
                <div>
                  <label className="block text-zinc-300 text-xs font-bold mb-1.5">Mobile number (10 digits)</label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                    placeholder="9876543210"
                    className="w-full bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-cyan-500/60"
                    data-testid="bundle-phone-input"
                  />
                </div>
              </div>

              {err && (
                <p className="text-red-300 text-xs bg-red-900/20 border border-red-500/30 rounded-lg p-2.5" data-testid="bundle-error">{err}</p>
              )}
            </div>
          )}
        </div>

        {/* Footer — live pricing + CTA */}
        <div className="border-t border-zinc-800 bg-zinc-950/60 p-4 sm:p-5">
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <div className="min-w-0">
              <p className="text-zinc-500 text-[10px] uppercase tracking-wider font-bold">{quote.course_count > 0 ? "Total" : "Select courses to see price"}</p>
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-2xl sm:text-3xl font-extrabold text-cyan-300" data-testid="bundle-total-price">
                  ₹{(quote.bundle_price_inr || 0).toLocaleString("en-IN")}
                </span>
                {quote.savings_inr > 0 && (
                  <>
                    <span className="text-zinc-500 text-sm line-through">₹{quote.individual_total_inr.toLocaleString("en-IN")}</span>
                    <span className="px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-[10px] font-bold whitespace-nowrap">
                      save ₹{quote.savings_inr.toLocaleString("en-IN")} ({quote.discount_pct}% off)
                    </span>
                  </>
                )}
                {quoteLoading && <span className="text-zinc-500 text-xs">updating…</span>}
              </div>
            </div>

            {step === 1 ? (
              <button
                onClick={() => setStep(2)}
                disabled={selected.size < 2}
                className="px-5 py-2.5 rounded-lg text-black font-bold text-sm disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                style={{ background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_DEEP})` }}
                data-testid="bundle-continue-btn"
              >
                Continue → {selected.size < 2 && <span className="opacity-70 text-[11px]">(pick ≥2)</span>}
              </button>
            ) : (
              <div className="flex gap-2 shrink-0">
                <button
                  onClick={() => setStep(1)}
                  disabled={submitting}
                  className="px-4 py-2.5 rounded-lg border border-zinc-700 text-zinc-300 hover:border-zinc-500 text-sm font-semibold disabled:opacity-50"
                  data-testid="bundle-back-btn"
                >
                  ← Back
                </button>
                <button
                  onClick={payAndRegister}
                  disabled={submitting || selected.size < 2}
                  className="px-5 py-2.5 rounded-lg text-black font-bold text-sm disabled:opacity-50 whitespace-nowrap"
                  style={{ background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_DEEP})` }}
                  data-testid="bundle-pay-btn"
                >
                  {submitting ? "Redirecting to payment…" : `Pay & Register · ₹${(quote.bundle_price_inr || 0).toLocaleString("en-IN")}`}
                </button>
              </div>
            )}
          </div>
          {step === 2 && (
            <p className="text-zinc-500 text-[11px]">Secure payment via Cashfree · UPI / cards / netbanking accepted · You&apos;ll get one email with magic links to all {quote.course_count} courses.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function SupportFooter() {
  return (
    <section className="rounded-2xl border border-zinc-800 bg-zinc-900/30 p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between" data-testid="support-footer">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-full bg-cyan-500/15 border border-cyan-500/40 flex items-center justify-center shrink-0">
          <MessageCircle size={18} className="text-cyan-300" />
        </div>
        <div>
          <p className="text-white text-sm font-bold">Stuck on a lecture or have a doubt?</p>
          <p className="text-zinc-400 text-xs">Email <a href="mailto:support@shansoai.com" className="text-cyan-300 underline">support@shansoai.com</a> · we reply within 24h on weekdays.</p>
        </div>
      </div>
      <Link
        to="/contact"
        className="shrink-0 px-4 py-2 rounded-lg border border-cyan-500/50 text-cyan-200 hover:bg-cyan-500/10 text-sm font-semibold"
        data-testid="support-contact-link"
      >
        Contact support
      </Link>
    </section>
  );
}

function GoogleSignInCTA() {
  function loginWithGoogle() {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    startGoogleLogin("/courses");
  }
  return (
    <section
      className="rounded-2xl border border-cyan-500/30 bg-gradient-to-br from-cyan-900/25 via-zinc-900/40 to-fuchsia-900/15 p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between"
      data-testid="google-signin-cta"
    >
      <div className="flex items-start gap-3 min-w-0">
        <div className="w-11 h-11 rounded-xl bg-white flex items-center justify-center shrink-0">
          <GoogleIcon />
        </div>
        <div className="min-w-0">
          <p className="text-white text-base sm:text-lg font-extrabold leading-tight">Already enrolled? Find your courses.</p>
          <p className="text-zinc-300 text-sm mt-1">Sign in with Google to see every course you&apos;ve purchased — across all your devices.</p>
        </div>
      </div>
      <button
        onClick={loginWithGoogle}
        className="shrink-0 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-white hover:bg-zinc-100 text-zinc-900 font-bold text-sm whitespace-nowrap transition-colors"
        data-testid="catalog-google-signin-btn"
      >
        <GoogleIcon /> Sign in with Google
      </button>
    </section>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 01-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.81 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.94v2.32A9 9 0 009 18z" />
      <path fill="#FBBC05" d="M3.97 10.72A5.41 5.41 0 013.68 9c0-.6.1-1.18.29-1.72V4.96H.94A9 9 0 000 9c0 1.45.35 2.83.94 4.04l3.03-2.32z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 009 0 9 9 0 00.94 4.96l3.03 2.32C4.68 5.16 6.66 3.58 9 3.58z" />
    </svg>
  );
}

function AccountPill({ user, onSignOut }) {
  const [open, setOpen] = useState(false);
  const initials = (user.name || user.email || "?")
    .split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (!e.target.closest('[data-testid="account-pill-wrap"]')) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div className="relative" data-testid="account-pill-wrap">
      <button
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 px-2 py-1.5 rounded-full bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 hover:border-zinc-700 transition-colors max-w-[200px]"
        data-testid="account-pill-btn"
      >
        {user.picture ? (
          <img src={user.picture} alt="" className="w-6 h-6 rounded-full" referrerPolicy="no-referrer" />
        ) : (
          <span className="w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-200 text-[10px] font-bold flex items-center justify-center">
            {initials}
          </span>
        )}
        <span className="hidden sm:inline text-zinc-200 text-xs font-semibold truncate max-w-[140px]">{user.name || user.email}</span>
        <ChevronDown size={12} className="text-zinc-400 shrink-0" />
      </button>
      {open && (
        <div
          className="absolute right-0 top-[calc(100%+6px)] w-64 bg-zinc-950 border border-zinc-800 rounded-xl shadow-2xl shadow-black/60 p-1.5 z-40"
          data-testid="account-pill-menu"
        >
          <div className="px-3 py-2.5 border-b border-zinc-800/80">
            <p className="text-white text-sm font-bold truncate" data-testid="account-name">{user.name || "Signed in"}</p>
            <p className="text-zinc-400 text-xs truncate" data-testid="account-email">{user.email}</p>
          </div>
          <button
            onClick={onSignOut}
            className="w-full mt-1 flex items-center gap-2 px-3 py-2 rounded-lg text-red-300 hover:bg-red-500/10 text-sm font-semibold transition-colors"
            data-testid="account-signout-btn"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}
